/* eslint-disable max-nested-callbacks */
import { Grid, Stack, Typography } from '@mui/material'
import MultiGridTableV2 from 'components/datatable/virtualizedTable/MultiGridTableV2'
import { groupBy, isNil, maxBy, meanBy, minBy, orderBy, round, uniqBy, keys } from 'lodash'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { calculateGeometricAverage, calculatePercentile90 } from 'utils/AnalyseUtils'
import { getDate } from 'utils/DateUtil'
import { hasValue } from 'utils/NumberUtil'
import { statusIcon } from 'utils/StatusUtil'
import { getLabel } from 'utils/StoreUtils'
import PropTypes from 'prop-types'
import DtoOperation from 'quality/components/operation/dto/DtoOperation'
import i18n from 'simple-react-i18n'
import { SPECIFIC_SUPPORT_CODES } from 'quality/constants/HydrobioConstant'
import useActions from 'utils/customHook/useActions'
import SliderPanel from 'components/slider/SliderPanel'
import useListIndexed from 'utils/customHook/useListIndexed'
import WidgetsIcon from '@mui/icons-material/Widgets'
import FaceIcon from '@mui/icons-material/Face'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { disabledColor } from 'utils/constants/ColorTheme'
import useBoolean from 'utils/customHook/useBoolean'
import { HydrobioGraphModal } from 'quality/components/qualityComponents/HydrobioGraph'

const STATS_HEADERS = ['min', 'averageShort', 'max', 'percentile90', 'geometricAverageShort', 'nbQuant', 'nbCounting', 'quantTx']

const HeaderCellTootip = ({
    operationId,
}) => {
    const {
        hydrobioOperations,
        contributorsIndex,
        status,
        qualifications,
    } = useSelector(store => ({
        hydrobioOperations: store.OperationReducer.hydrobioOperations,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        status: store.QualityReducer.status,
        qualifications: store.QualityReducer.qualifications,
    }), shallowEqual)

    const operation = hydrobioOperations.find(({ id }) => `${id}` === operationId)
    const producer = contributorsIndex[operation.producer]
    const producerLabel = producer && (producer.mnemonique || producer.name) || ''

    const determiner = contributorsIndex[operation.determiner]
    const determinerLabel = determiner && (determiner.mnemonique || determiner.name) || ''

    return (
        <Stack>
            <Typography>
                {`${i18n.date}: ${getDate(operation.date)}`}
            </Typography>
            <Typography>
                {`${i18n.status}: ${getLabel(status, operation.status)}`}
            </Typography>
            <Typography>
                {`${i18n.qualification}: ${getLabel(qualifications, operation.qualification)}`}
            </Typography>
            <Typography>
                {`${i18n.producer}: ${producerLabel}`}
            </Typography>
            <Typography>
                {`${i18n.determiner}: ${determinerLabel}`}
            </Typography>
        </Stack>
    )
}

HeaderCellTootip.propTypes = {
    operationId: PropTypes.string,
}

const HeaderCell = ({
    operationId,
}) => {
    const {
        hydrobioOperations,
        contributorsIndex,
    } = useSelector(store => ({
        hydrobioOperations: store.OperationReducer.hydrobioOperations,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
    }), shallowEqual)

    const operation = hydrobioOperations.find(({ id }) => `${id}` === operationId)
    const producer = contributorsIndex[operation.producer]
    const producerLabel = producer && (producer.mnemonique || producer.name) || ''

    const determiner = contributorsIndex[operation.determiner]
    const determinerLabel = determiner && (determiner.mnemonique || determiner.name) || ''

    return (
        <Stack justifyContent='center' alignItems='flex-start'>
            <Grid justifyContent='center' alignItems='center'>
                {statusIcon(operation, 15, false)}
                <span style={{ paddingLeft: 5, fontSize: '12px' }}> {getDate(operation.date)}</span>
            </Grid>
            <Grid justifyContent='center' alignItems='center'>
                <WidgetsIcon sx={{ fontSize: 15 }}/>
                <span style={{ paddingLeft: 5, fontSize: '12px' }}>{producerLabel}</span>
            </Grid>
            <Grid justifyContent='center' alignItems='center'>
                <FaceIcon sx={{ fontSize: 15 }}/>
                <span style={{ paddingLeft: 5, fontSize: '12px' }}>{determinerLabel}</span>
            </Grid>
        </Stack>
    )
}

HeaderCell.propTypes = {
    operationId: PropTypes.string,
}

const TooltipTaxon = ({
    list = {},
}) => {
    const {
        remarks,
        taxons,
    } = useSelector(store => ({
        remarks: store.OperationReducer.remarks,
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const indexedTaxons = useListIndexed(taxons)

    const {
        taxon,
        enumerationA = 0,
        enumerationB = 0,
        enumerationC = 0,
        enumerationC2 = 0,
        total,
        remark,
        support,
        index,
        threshold,
    } = list

    const isSpecific = SPECIFIC_SUPPORT_CODES.includes(support)

    return (
        <Grid container direction='column' justifyContent='center' alignItems='flex-start'>
            <Grid>
                {`${i18n.remark}: ${getLabel(remarks, remark)}`}
            </Grid>
            {
                !isSpecific && (
                    <>
                        <Grid>
                            {`${i18n.phaseA}: ${enumerationA}`}
                        </Grid>
                        <Grid>
                            {`${i18n.phaseB}: ${enumerationB}`}
                        </Grid>
                        <Grid>
                            {`${i18n.phaseC}: ${enumerationC}`}
                        </Grid>
                        <Grid>
                            {`${i18n.phaseCbis}: ${enumerationC2}`}
                        </Grid>
                    </>
                )
            }
            <Grid>
                {`${i18n.total}: ${total}`}
            </Grid>
            {remark !== '0' && !isNil(index) && threshold && (
                <Grid>
                    <SliderPanel
                        title={indexedTaxons[taxon]?.latinName ?? i18n.defaultThreshold}
                        analysis={list}
                        threshold={threshold}
                        withThresholdLevels
                    />
                </Grid>
            )}
        </Grid>
    )
}

TooltipTaxon.propTypes = {
    list: PropTypes.shape({
        enumerationA: PropTypes.number,
        enumerationB: PropTypes.number,
        enumerationC: PropTypes.number,
        enumerationC2: PropTypes.number,
        total: PropTypes.number,
        remarkCode: PropTypes.number,
    }),
}

const HydrobioMonitoringTable = ({
    hydrobioLists = [],
    hydrobioOperations = [],
}) => {
    const {
        taxons,
        supports,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        supports: store.SupportReducer.supports,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const {
        value: isGraphOpen,
        setTrue: openGraph,
        setFalse: closeGraph,
    } = useBoolean(false)
    const [selectedTaxon, setSelectedTaxon] = useState()

    // todo use id qualito as well as idOperation
    const uniqOperations = useMemo(() => {
        return uniqBy(hydrobioLists, ({ operation }) => operation)
    }, [hydrobioLists])

    const operationsFound = useMemo(() => {
        return uniqOperations.map(({ operation }) => hydrobioOperations.find(op => op.id === operation))
    }, [uniqOperations, hydrobioOperations])

    const operationsDatesSort = useMemo(() => {
        return orderBy(operationsFound, ['date', 'qualitometer'], ['desc', 'asc']).map(op => `${op.id}`)
    }, [operationsFound])

    const data = useMemo(() => {
        const listGroupByTaxon = groupBy(hydrobioLists, ({ taxon }) => taxon)
        return keys(listGroupByTaxon).map(taxonCode => {
            const { [taxonCode]: listGroup = [] } = listGroupByTaxon

            const valuesObj = listGroup.reduce((acc, list) => {
                const key = `${list.operation}`
                acc[key] = {
                    justifyContent: 'right',
                    value: list.total,
                    sortValue: list.total,
                    tooltip: (<TooltipTaxon list={list}/>),
                    color: list.color,
                }
                return acc
            }, {})

            const taxon = taxons.find(t => t.code === taxonCode)
            const groupSupport = uniqBy(listGroup, 'support')
            const allSupport = groupSupport.map(({ support }) => showSandreCode ? supports.find(u => u.code == support)?.labelWithCode : supports.find(u => u.code == support)?.name)
            const cleanSupport = allSupport.filter(u => !!u).join(', ')

            const listTotal = listGroup.map(l => l.total)
            const minValue = minBy(listGroup, 'total')
            const maxValue = maxBy(listGroup, 'total')
            const averageValue = meanBy(listGroup, 'total')
            const sortListGroup = orderBy(listGroup, 'total')
            const percentileValue = calculatePercentile90(sortListGroup)
            const geoAverage = calculateGeometricAverage(listGroup.map(l => l.total))
            const nbCounting = listTotal.length
            const nbQuant = listGroup.filter(l => hasValue(l.remark) && !([0, 6, 8, 9].includes(l.remark))).length
            const quantTx = listGroup.length ? `${round(nbQuant * 100 / nbCounting, 2)} %` : '0%'

            return {
                ...valuesObj,
                taxon: {
                    value: taxon?.latinName,
                    tooltip: taxon?.latinName || undefined,
                },
                support: {
                    value: cleanSupport,
                    tooltip: cleanSupport || undefined,
                },
                min: {
                    value: minValue?.total,
                    color: disabledColor,
                    justifyContent: 'right',
                    tooltip: (<TooltipTaxon list={minValue} />),
                },
                max: {
                    value: maxValue?.total,
                    color: disabledColor,
                    justifyContent: 'right',
                    tooltip: (<TooltipTaxon list={maxValue} />),
                },
                averageShort: {
                    value: round(averageValue, 3),
                    color: disabledColor,
                    justifyContent: 'right',
                },
                geometricAverageShort: {
                    value: round(geoAverage, 3),
                    color: disabledColor,
                    justifyContent: 'right',
                },
                percentile90: {
                    value: percentileValue?.total,
                    color: disabledColor,
                    justifyContent: 'right',
                    tooltip: (<TooltipTaxon list={percentileValue} />),
                },
                nbCounting: {
                    value: nbCounting,
                    color: disabledColor,
                    justifyContent: 'right',
                },
                nbQuant: {
                    value: nbQuant,
                    color: disabledColor,
                    justifyContent: 'right',
                },
                quantTx: {
                    value: quantTx,
                    color: disabledColor,
                    justifyContent: 'right',
                },
                taxonCode,
            }
        })
    }, [hydrobioLists, supports, taxons, showSandreCode])

    const customHeaders = useMemo(() => {
        const operationHeaders = operationsDatesSort.reduce((acc, id) => {
            acc[id] = {
                value: (<HeaderCell operationId={id} />),
                tooltip: (<HeaderCellTootip operationId={id} />),
            }
            return acc
        }, {})

        const statHeaders = {
            nbCounting: {
                style: {
                    whiteSpace: 'pre-wrap',
                },
            },
        }
        return {
            ...operationHeaders,
            ...statHeaders,
        }
    }, [operationsDatesSort])

    useActions(() => {
        return {
            export: () => {
                const listGroupByTaxon = groupBy(hydrobioLists, ({ taxon }) => taxon)
                const exportData = keys(listGroupByTaxon).map(taxonCode => {
                    const { [taxonCode]: listGroup = [] } = listGroupByTaxon

                    const valuesObj = listGroup.reduce((acc, list) => {
                        const key = `${list.operation}`
                        acc[key] = {
                            positionCell: 'right',
                            value: list.total,
                        }
                        return acc
                    }, {})

                    const taxon = taxons.find(t => t.code === taxonCode)
                    const supportCodes = uniqBy(listGroup, 'support')

                    const listTotal = listGroup.map(l => l.total)
                    const minValue = minBy(listGroup, 'total')
                    const maxValue = maxBy(listGroup, 'total')
                    const averageValue = meanBy(listGroup, 'total')
                    const sortListGroup = orderBy(listGroup, 'total')
                    const percentileValue = calculatePercentile90(sortListGroup)
                    const geoAverage = calculateGeometricAverage(listGroup.map(l => l.total))
                    const nbCounting = listTotal.length
                    const nbQuant = listGroup.filter(l => hasValue(l.remark) && !([0, 6, 8, 9].includes(l.remark))).length
                    const quantTx = listGroup.length ? `${round(nbQuant * 100 / nbCounting, 2)} %` : '0%'

                    return {
                        taxonCode,
                        taxon: taxon?.latinName ?? '',
                        supportCode: supportCodes.join(', '),
                        support: supportCodes.map(({ support }) => supports.find(u => u.code == support)?.name).filter(u => !!u).join(', '),
                        ...valuesObj,
                        min: {
                            value: minValue?.total,
                            color: 'gray',
                            positionCell: 'right',
                        },
                        max: {
                            value: maxValue?.total,
                            color: 'gray',
                            positionCell: 'right',
                        },
                        averageShort: {
                            value: round(averageValue, 3),
                            color: 'gray',
                            positionCell: 'right',
                        },
                        geometricAverageShort: {
                            value: round(geoAverage, 3),
                            color: 'gray',
                            positionCell: 'right',
                        },
                        percentile90: {
                            value: percentileValue?.total,
                            color: 'gray',
                            positionCell: 'right',
                        },
                        nbCounting: {
                            value: nbCounting,
                            color: 'gray',
                            positionCell: 'right',
                        },
                        nbQuant: {
                            value: nbQuant,
                            color: 'gray',
                            positionCell: 'right',
                        },
                        quantTx: {
                            value: quantTx,
                            color: 'gray',
                            positionCell: 'right',
                        },
                    }
                })
                const exportHeaders = ['taxonCode', 'taxon', 'supportCode', 'support', ...operationsDatesSort, 'min', 'max', 'averageShort', 'geometricAverageShort', 'percentile90', 'nbCounting', 'nbQuant', 'quantTx']
                const exportCustomHeaders = ['taxonCode', 'taxon', 'supportCode', 'support', ...orderBy(operationsFound, ['date', 'qualitometer'], ['desc', 'asc']).map(o => getDate(o.date)), 'min', 'max', 'averageShort', 'geometricAverageShort', 'percentile90', 'nbCounting', 'nbQuant', 'quantTx']
                return {
                    data: exportData.length ? [{ ...exportData[0], headers: exportHeaders, customHeaders: exportCustomHeaders }, ...exportData.slice(1)] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.hydrobioMonitoring,
                }
            },
        }
    }, [operationsFound, hydrobioLists, taxons, supports])

    const filteredHydrobioLists = hydrobioLists
        .filter(l => `${l.taxon}` === `${selectedTaxon}`)
        .map(l => ({ ...l, operationDate: l.operationDate ?? l.date, result: l.result ?? l.total }))

    return (
        <>
            <MultiGridTableV2
                data={data}
                customHeaders={customHeaders}
                headers={['taxon', 'support', ...operationsDatesSort, ...STATS_HEADERS]}
                fixedColumnCount={2}
                headerHeight={60}
                onClick={({ taxonCode }) => {
                    setSelectedTaxon(taxonCode)
                    openGraph()
                }}
            />
            <HydrobioGraphModal
                isOpen={isGraphOpen}
                hydrobioList={filteredHydrobioLists}
                closeGraph={closeGraph}
            />
        </>
    )
}

HydrobioMonitoringTable.propTypes = {
    hydrobioLists: PropTypes.arrayOf(PropTypes.shape({
        // DtoHydrobioList
        date: PropTypes.number,
        support: PropTypes.string,
        total: PropTypes.number,
    })),
    hydrobioOperations: PropTypes.arrayOf(PropTypes.instanceOf(DtoOperation)),
}

export default HydrobioMonitoringTable